import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  useTheme,
  Box,
  Typography,
  Divider,
  useMediaQuery,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import CommentRoundedIcon from "@mui/icons-material/CommentRounded";
import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";
import api, { isAbortedRequestError } from "services/api";
import Tabs from "@mui/material/Tabs";
import { OVERLAY_NAMES } from "constants/Overlays";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { a11yProps, CustomTabPanel } from "components/CustomTabPanel";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import CreditScoreRoundedIcon from "@mui/icons-material/CreditScoreRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import CloudIcon from "@mui/icons-material/CloudRounded";
import TaskRoundedIcon from "@mui/icons-material/TaskRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import {
  formatAmount,
  formatDateLong,
  formatDateLongWithTime,
  getStatusColor,
} from "helpers/ServiceHelpers";
import { Card } from "components/Card";
import { Card2 } from "components/Card2";
import { NoteCard } from "components/NoteCard";
import { useStateContext } from "contexts/ContextProvider";
import dayjs from "dayjs";
import { useAuth } from "../../../contexts/AuthContext";
import CustomTab from "components/CustomTab";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import History from "../History";
import TaskItem from "components/TaskItem";
import { TaskBoard, buildTaskQueryParams } from "../../workspace/tasks/Tasks";
import { DragDropContext, Draggable } from "@hello-pangea/dnd";
import { usePageTitle } from "hooks/usePageTitle";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { useWebSocket } from "contexts/WebSocketContext";
import { NAVBAR_HEIGHT } from "constants/layout";

const formatCurrencyValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "$0";
  }

  return `$${value}`;
};

const OtherServiceProfile = () => {
  const SERVICE_PROFILE_VIEW_KEY = "SERVICE_PROFILE_VIEW_MODE";
  const [pageTitle, setPageTitle] = useState("Other Service");
  usePageTitle(pageTitle);

  const { currentUser } = useAuth();
  const canUseModernDetailsView = false;
  const { socket } = useWebSocket();

  const [tabValue, setTabValue] = useState(0);
  const [mobileTabValue, setMobileTabValue] = useState(0);
  const [useModernDetailsView] = useState(() => {
    try {
      if (!canUseModernDetailsView) return false;
      return localStorage.getItem(SERVICE_PROFILE_VIEW_KEY) === "modern";
    } catch (error) {
      console.error("Failed to read service profile view mode:", error);
      return false;
    }
  });
  const isModernDetailsEnabled =
    canUseModernDetailsView && useModernDetailsView;
  const showMobileOverview = !isModernDetailsEnabled;

  useEffect(() => {
    if (!canUseModernDetailsView) return;
    localStorage.setItem(
      SERVICE_PROFILE_VIEW_KEY,
      useModernDetailsView ? "modern" : "classic",
    );
  }, [canUseModernDetailsView, useModernDetailsView]);

  // Refs for mobile scroll-to-view and scroll spy
  const overviewRef = useRef(null);
  const detailsRef = useRef(null);
  const tasksRef = useRef(null);
  const notesRef = useRef(null);
  const actionsRef = useRef(null);
  const historyRef = useRef(null);
  const mobileScrollRef = useRef(null);
  const mobileTabRef = useRef(0);

  const mobileSectionRefs = useMemo(
    () => [overviewRef, detailsRef, tasksRef, notesRef, actionsRef, historyRef],
    [],
  );

  const [taskStatuses, setTaskStatuses] = useState([]);
  const [tasksForService, setTasksForService] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);

  const handleTabsChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMobileTabsChange = (event, newValue) => {
    setMobileTabValue(newValue);
    mobileTabRef.current = newValue;
    const sectionRef =
      mobileSectionRefs[newValue + (showMobileOverview ? 0 : 1)];
    if (sectionRef?.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    const maxTabIndex = showMobileOverview ? 5 : 4;
    if (mobileTabRef.current > maxTabIndex) {
      mobileTabRef.current = maxTabIndex;
      setMobileTabValue(maxTabIndex);
    }
  }, [showMobileOverview]);

  const { id } = useParams();

  const navigate = useNavigate();

  const location = useLocation();
  const data = location.state;
  const theme = useTheme();

  const {
    refreshPage,
    triggerPageRefresh,
    showSnackbar,
    openDialog,
    handleErrorDialog,
    closeDialog,
  } = useStateContext();
  const { setOverlayName, overlayData, setOverlayData } = useStateContext();

  const [listOfClients, setListOfClients] = useState([]);
  const [listOfLinguists, setListOfLinguists] = useState([]);
  const [listOfLanguages, setListOfLanguages] = useState([]);
  const [listOfStatuses, setListOfStatuses] = useState([]);
  const [listOfTypes, setListOfTypes] = useState([]);
  const [latestProjectNumber, setLatestProjectNumber] = useState(0);
  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [serviceHistories, setServiceHistories] = useState([]);

  const [otherServiceData, setOtherServiceData] = useState({});
  const [serviceCostSummary, setServiceCostSummary] = useState(null);
  const [showOtherCosts, setShowOtherCosts] = useState(false);

  // Track last update for snackbar notification
  const [lastUpdate, setLastUpdate] = useState(null);

  const getDataLists = useCallback(() => {
    api
      .all([
        api.get("/clients/brief"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/otherServices/statuses"),
        api.get("/services/otherServices/types"),
        api.get("/services/otherServices/latestProjectNumber"),
        api.get("/services/translations/deliveryTypes"),
        api.get("/services/translations/paymentTypes"),
        api.get("/auditLogs/entity/Service/" + id, { params: { limit: 1000 } }),
      ])
      .then(
        api.spread(
          (
            clientsResponse,
            linguistsResponse,
            languagesResponse,
            statusesResponse,
            typesResponse,
            latestProjectNumberResponse,
            deliveryTypesResponse,
            paymentTypesResponse,
            serviceHistoriesResponse,
          ) => {
            if (clientsResponse) {
              setListOfClients(clientsResponse.data);
            }
            if (linguistsResponse) {
              setListOfLinguists(linguistsResponse.data);
            }
            if (languagesResponse) {
              const tempLanguages = languagesResponse.data.filter(
                (language) => language.Name !== "ALL",
              );
              setListOfLanguages(tempLanguages);
            }
            if (statusesResponse) {
              setListOfStatuses(statusesResponse.data);
            }
            if (typesResponse) {
              setListOfTypes(typesResponse.data);
            }
            if (
              latestProjectNumberResponse &&
              latestProjectNumberResponse.data
            ) {
              setLatestProjectNumber(
                Number(latestProjectNumberResponse.data.ProjectNumber || 0) + 1,
              );
            }
            if (deliveryTypesResponse) {
              const filteredDeliveryTypes = deliveryTypesResponse.data.filter(
                (type) => !["CODA", "SmartCat", "In-person"].includes(type),
              );
              setDeliveryTypes(filteredDeliveryTypes);
            }
            if (paymentTypesResponse) {
              setPaymentTypes(paymentTypesResponse.data);
            }
            if (serviceHistoriesResponse) {
              setServiceHistories(
                serviceHistoriesResponse.data?.auditLogs || [],
              );
            }
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";

        handleErrorDialog(message, theme);
      });
  }, [id, handleErrorDialog, theme]);

  const fetchServiceCostData = useCallback(() => {
    api
      .get(`/serviceCosts/service/${id}/lines`)
      .then((response) => {
        setServiceCostSummary(response.data?.summary || null);
      })
      .catch((error) => {
        console.error("Error fetching service cost lines:", error);
        setServiceCostSummary(null);
      });
  }, [id]);

  useEffect(() => {
    const fetchData = () => {
      api.get(`/services/otherServices/${id}`).then((response) => {
        const otherService = response.data;
        setPageTitle("Other Service: " + (otherService.ProjectNumber || "N/A"));

        setOtherServiceData(otherService);
        fetchServiceCostData();
        getDataLists();
      }).catch((error) => {
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        if (error.response?.status === 404) {
          navigate("/404", { replace: true });
        } else {
          handleErrorDialog(message, theme);
        }
      });
    };
    fetchData();
  }, [id, navigate, refreshPage, getDataLists, fetchServiceCostData]);

  // Set entity tracking for presence and updates
  const { setCurrentEntity } = usePresence();
  usePagePresence(); // Join entity room when entity is set

  useEffect(() => {
    if (id) {
      setCurrentEntity({
        entityType: "OtherService",
        entityId: parseInt(id),
      });
    }
    return () => {
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [id, setCurrentEntity]);

  // Listen for assignment updates from other members
  const handleAssignmentRefetch = useCallback(
    (updatedBy, meta = {}) => {
      // Reload the assignment data
      api
        .get(`/services/otherServices/${id}`)
        .then((response) => {
          setOtherServiceData(response.data);
          fetchServiceCostData();
          setLastUpdate({
            userName: updatedBy?.userName || "Another member",
            changeType: meta?.changeType,
            changeAction: meta?.changeAction,
            projectNumber: meta?.projectNumber,
          });
        })
        .catch((err) => {
          if (err.response?.status === 404) {
            navigate("/404", { replace: true });
          } else {
            console.log("Error refetching assignment:", err);
          }
        });
    },
    [id, fetchServiceCostData, navigate],
  );

  useAssignmentUpdates({
    modelType: "OtherService",
    recordId: id ? parseInt(id) : null,
    onUpdate: handleAssignmentRefetch,
  });

  // Show snackbar when update is received (in separate effect to avoid listener churn)
  useEffect(() => {
    if (lastUpdate?.userName) {
      const action = lastUpdate.changeAction || "Updated";
      const changeType = lastUpdate.changeType || "Other Service";
      const message = `${changeType} ${action} by ${lastUpdate.userName}. Check the History for more details`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  useEffect(() => {
    const fetchTaskEnums = async () => {
      try {
        const [statusesRes] = await Promise.all([
          api.get("/tasks/enums/statuses"),
        ]);
        setTaskStatuses(statusesRes.data || []);
      } catch (error) {
        console.error("Error fetching task enums:", error);
      }
    };

    fetchTaskEnums();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchTasksForService = async () => {
      if (!id) return;
      setTasksLoading(true);
      try {
        const params = buildTaskQueryParams(
          {
            entityServiceTypes: ["Other Service"],
            showAllCompleted: true,
          },
          currentUser?.id,
        );
        params.append("entityId", id);

        const response = await api.get(`/tasks?${params.toString()}`, {
          signal: controller.signal,
        });
        setTasksForService(response.data || []);
      } catch (error) {
        if (isAbortedRequestError(error)) {
          return;
        }
        console.error("Error fetching tasks for service:", error);
        handleErrorDialog("Failed to load tasks", theme);
      } finally {
        if (!controller.signal.aborted) {
          setTasksLoading(false);
        }
      }
    };

    fetchTasksForService();

    return () => {
      controller.abort();
    };
  }, [id, refreshPage, currentUser?.id, handleErrorDialog, theme]);

  // Listen for task updates linked to this service
  useEffect(() => {
    if (!socket || !id) return;

    const handleTaskUpdate = async (data) => {
      // Check if task is linked to this service
      if (
        data.entityType === "Service" &&
        data.serviceType === "Other Service" &&
        data.entityID === parseInt(id)
      ) {
        // Refetch tasks for this service
        try {
          const params = buildTaskQueryParams(
            {
              entityServiceTypes: ["Other Service"],
              showAllCompleted: true,
            },
            currentUser?.id,
          );
          params.append("entityId", id);

          const response = await api.get(`/tasks?${params.toString()}`);
          setTasksForService(response.data || []);

          // Show snackbar notification
          const userName = data.updatedBy?.userName || "Unknown User";
          const message = `Task ${data.changeAction} by ${userName}`;
          showSnackbar(message, theme, "info");
        } catch (error) {
          if (!isAbortedRequestError(error)) {
            console.error("Error refreshing tasks:", error);
          }
        }
      }
    };

    socket.on("task:updated", handleTaskUpdate);
    return () => socket.off("task:updated", handleTaskUpdate);
  }, [socket, id, currentUser?.id, showSnackbar, theme]);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  // Scroll spy effect to update active tab based on scroll position
  useEffect(() => {
    if (!isSmallScreen || !mobileScrollRef.current) return;

    const scrollContainer = mobileScrollRef.current;
    const offset = 88;
    let rafId = null;

    const updateActiveTab = () => {
      const scrollTop = scrollContainer.scrollTop;
      let activeIndex = 0;

      mobileSectionRefs.forEach((ref, index) => {
        if (!ref.current) return;
        const sectionTop = ref.current.offsetTop;
        if (sectionTop <= scrollTop + offset) {
          activeIndex = index;
        }
      });

      if (activeIndex !== mobileTabRef.current) {
        const tabIndex = Math.max(
          activeIndex - (showMobileOverview ? 0 : 1),
          0,
        );
        mobileTabRef.current = tabIndex;
        setMobileTabValue(tabIndex);
      }
    };

    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateActiveTab();
      });
    };

    updateActiveTab();
    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [isSmallScreen, mobileSectionRefs, showMobileOverview]);

  const groupTasksByStatus = (taskList) => {
    const grouped = {};
    taskStatuses.forEach((status) => {
      grouped[status] = [];
    });

    taskList.forEach((task) => {
      if (!grouped[task.Status]) {
        grouped[task.Status] = [];
      }
      grouped[task.Status].push(task);
    });

    return grouped;
  };

  const flattenTasksByStatus = (groupedTasks) => {
    const ordered = [];

    taskStatuses.forEach((status) => {
      if (groupedTasks[status]) {
        ordered.push(...groupedTasks[status]);
      }
    });

    Object.keys(groupedTasks).forEach((status) => {
      if (!taskStatuses.includes(status)) {
        ordered.push(...groupedTasks[status]);
      }
    });

    return ordered;
  };

  const handleTaskDragEnd = async (result) => {
    const { destination, draggableId } = result;

    if (!destination) {
      return;
    }

    const newStatus = destination.droppableId;
    const taskId = draggableId.replace("task-", "");

    setTasksForService((prevTasks) => {
      const taskToMove = prevTasks.find(
        (task) => task.id.toString() === taskId,
      );
      if (!taskToMove) return prevTasks;

      const withoutTask = prevTasks.filter(
        (task) => task.id.toString() !== taskId,
      );
      const tasksByStatus = groupTasksByStatus(withoutTask);

      const updatedTask = { ...taskToMove, Status: newStatus };
      const destinationList = tasksByStatus[newStatus] || [];
      const insertAt = Math.min(destination.index, destinationList.length);
      destinationList.splice(insertAt, 0, updatedTask);
      tasksByStatus[newStatus] = destinationList;

      return flattenTasksByStatus(tasksByStatus);
    });

    try {
      await api.patch(`/tasks/updateStatus/${taskId}`, {
        Status: newStatus,
      });

      // Emit socket event for real-time updates
      if (socket) {
        const taskToUpdate = tasksForService.find(
          (t) => t.id.toString() === taskId,
        );
        if (taskToUpdate) {
          socket.emit("task:saved", {
            modelType: "Task",
            recordId: parseInt(taskId),
            entityType: taskToUpdate.EntityType,
            entityID: taskToUpdate.EntityID,
            serviceType: taskToUpdate.ServiceType,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      handleErrorDialog("Failed to update task status", theme);

      const params = buildTaskQueryParams(
        {
          entityServiceTypes: ["Interpretation"],
          showAllCompleted: true,
        },
        currentUser?.id,
      );
      params.append("entityId", id);
      const response = await api.get(`/tasks?${params.toString()}`);
      setTasksForService(response.data || []);
    }
  };

  const renderTaskCard = (task, index) => (
    <Draggable
      draggableId={`task-${task.id}`}
      index={index}
      key={task.id}
      disableInteractiveElementBlocking
    >
      {(provided, snapshot) => (
        <TaskItem
          key={task.id}
          task={task}
          onClick={() => {
            setOverlayData({ Task: task });
            setOverlayName(OVERLAY_NAMES.TASK_PROFILE);
          }}
          provided={provided}
          snapshot={snapshot}
        />
      )}
    </Draggable>
  );

  const languages = otherServiceData.ServiceTargetLanguages;

  const linguistName = otherServiceData.Linguist?.Name?.trim() || "";
  const normalizedLinguistName = linguistName.toLowerCase();
  const isLinguistUnavailable =
    !linguistName ||
    normalizedLinguistName === "n/a" ||
    normalizedLinguistName === "na" ||
    normalizedLinguistName === "none" ||
    normalizedLinguistName === "null" ||
    normalizedLinguistName === "undefined" ||
    normalizedLinguistName === "-";

  const clientID = otherServiceData.Client?.id || null;
  const linguistID = isLinguistUnavailable
    ? null
    : otherServiceData.Linguist?.id || null;

  const targetLanguages =
    !languages || languages.length === 0
      ? "N/A"
      : languages
          .map((item) =>
            item.Language && item.Language.Name ? item.Language.Name : "",
          )
          .filter((name) => name)
          .join(" | ");

  const breakdown = serviceCostSummary?.componentBreakdown || {};
  const accountChargeValue = otherServiceData.AccountCharge;
  // Manual mode (DisableAutoChargeCalc): the stored TotalCost is the user's
  // deliberate override — the cost-line sum can lag behind it (2026-09-21).
  const totalCostValue = otherServiceData.DisableAutoChargeCalc
    ? otherServiceData.TotalCost ?? serviceCostSummary?.totalCostFromLines
    : serviceCostSummary?.totalCostFromLines || otherServiceData.TotalCost;
  const linguistPayValue =
    breakdown.LinguistPay || otherServiceData.LinguistPay;
  const additionalCostValue =
    breakdown.AdditionalCost || otherServiceData.AdditionalCost;
  const notarizationCostValue =
    breakdown.NotarizationOrCOTCost || otherServiceData.NotarizationOrCOTCost;
  const deliveryCostValue =
    breakdown.DeliveryCost || otherServiceData.DeliveryCost;
  const paymentFeesValue =
    breakdown.PaymentFees || otherServiceData.PaymentFees;

  const listOfDetails = [
    {
      title: "Client Data",
      items: [
        {
          subtitle: "Client",
          content: otherServiceData.Client?.Account || "N/A",
          onClick: clientID
            ? () =>
                navigate(`/clients/${clientID}`, {
                  state: {
                    ComingFrom: `/otherServices/${id}`,
                  },
                })
            : null,
        },
        {
          subtitle: "Requestor",
          content: otherServiceData.Requestor?.Name || "N/A",
        },
        {
          subtitle: "Invoicer",
          content: otherServiceData.Invoicer?.Name || "N/A",
        },
      ],
    },
    {
      title: "Linguist Data",
      items: [
        {
          subtitle: "Linguist",
          content: isLinguistUnavailable
            ? "N/A"
            : otherServiceData.Linguist?.Name,
          onClick: linguistID
            ? () =>
                navigate(`/linguists/${linguistID}`, {
                  state: {
                    ComingFrom: `/otherServices/${id}`,
                  },
                })
            : null,
        },
        {
          subtitle: "Source Language",
          content: otherServiceData.Language?.Name || "N/A",
        },
        {
          subtitle: "Rate",
          content: formatAmount(otherServiceData.Rate) || "N/A",
        },
      ],
    },
    {
      title: otherServiceData.UnitType?.Name || "Units",
      items: [
        {
          subtitle: "Unit Type",
          content: otherServiceData.UnitType?.Name || "N/A",
        },
        {
          subtitle: "Unit Count",
          content:
            otherServiceData.UnitCount || otherServiceData.WordCount || "N/A",
        },
        {
          subtitle: "Number Of Languages",
          content: otherServiceData.NumberOfLanguages || "N/A",
        },
        {
          subtitle: "Total Unit Count",
          content:
            Number(
              otherServiceData.UnitCount || otherServiceData.WordCount || 0,
            ) * Number(otherServiceData.NumberOfLanguages) || "N/A",
        },
      ],
    },
    {
      title: "Assignment",
      items: [
        { subtitle: "Name", content: otherServiceData.Name || "N/A" },
        {
          subtitle: "Type",
          content: otherServiceData.ServiceSubtype?.Name || "N/A",
        },
        {
          subtitle: "Delivery Date",
          content: otherServiceData.DeliveryDate || "N/A",
        },
      ],
    },
    {
      title: "More Details",
      items: [
        {
          subtitle: "Delivery Type",
          content: otherServiceData.DeliveryType,
        },
        {
          subtitle: "Payment Type",
          content: otherServiceData.PaymentType,
        },
        { subtitle: "Rate Type", content: otherServiceData.RateType || "N/A" },
      ],
    },
    {
      title: "Statuses",
      items: [
        {
          subtitle: "Paid",
          content: (
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {otherServiceData.Paid ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          ),
        },
        {
          subtitle: "Invoiced",
          content: (
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {otherServiceData.Invoiced ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          ),
        },
        {
          subtitle: "Cancelled",
          content: (
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {otherServiceData.Cancelled ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          ),
        },
      ],
    },
    {
      title: "Members",
      items: [
        {
          subtitle: "Created By",
          content: otherServiceData.Creator?.Name || "N/A",
        },
        {
          subtitle: "Checked By",
          content: otherServiceData.Checker?.Name || "N/A",
        },
        {
          subtitle: "Checked On",
          content: otherServiceData.CheckingDate
            ? dayjs.tz(otherServiceData.CheckingDate).format("M/D/YYYY h:mm A")
            : "N/A",
        },
      ],
    },
  ];

  const invoiceNumber = otherServiceData.Invoice?.Number || null;
  const invoiceID = otherServiceData.Invoice?.id || null;
  const hasLinkedInvoice = Boolean(invoiceID || otherServiceData.InvoiceID);
  const canMarkAsPaid = Boolean(
    !otherServiceData.Paid && !otherServiceData.Invoiced,
  );
  const canMarkAsUnpaid = Boolean(otherServiceData.Paid && !hasLinkedInvoice);

  if (otherServiceData.CancellationReason) {
    listOfDetails.push({
      title: "Cancellation",
      items: [
        {
          subtitle: "",
          content: otherServiceData.CancellationReason,
          multiline: true,
          centerContent: true,
        },
      ],
    });
  }

  const renderLegacyDetails = () => (
    <Grid container spacing={2}>
      {listOfDetails.map((detail, index) => (
        <Grid key={index} size={{ xs: 12, lg: 12, xl: 6 }}>
          <Card2 title={detail.title} items={detail.items} justify="start" />
        </Grid>
      ))}
    </Grid>
  );

  // NOTES MENU AND HANDLERS

  const [selectedServiceNote, setSelectedServiceNote] = useState(null);

  // Handler for editing using the existing logic.
  const handleEdit = (serviceNote) => {
    const data = {
      NoteID: serviceNote.id,
      Note: serviceNote.Note,
      ServiceID: otherServiceData.id,
      MemberID: currentUser.id,
      ModelType: "OtherService",
      Action: OVERLAY_NAMES.SERVICE_NOTE_ACTION_EDIT,
    };
    setOverlayData(data);
    setOverlayName(OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT);
  };

  // Delete action (2026-09-23): called from the note card's 3-dots menu with
  // the note directly (the old right-click context menu was undiscoverable —
  // notes looked uneditable). Matches the Notes pattern on Client/Linguist
  // profiles.
  const handleDelete = (serviceNote) => {
    setSelectedServiceNote(serviceNote);

    openDialog({
      theme: theme,
      title: "Confirm Delete",
      message: "Are you sure you want to delete this note?",
      //TO DO ADD dialogIconDelete
      customContent: null,

      tertiaryAction: null,
      secondaryAction: "Close",
      primaryAction: "Delete",

      tertiaryOnClick: null,

      secondaryOnClick: () => {
        closeDialog();
      },

      primaryOnClick: () => {
        deleteNote(serviceNote);
        closeDialog();
      },
    });
  };

  const markAssignmentAsChecked = () => {
    const data = {
      id: otherServiceData.id,

      CheckedBy: currentUser.id,
      CheckingDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    };

    api
      .post("/services/markServiceChecked", {
        data: data,
      })
      .then((response) => {
        triggerPageRefresh("Translation Assignment Marked as Checked", theme);
        closeDialog();
      })
      .catch((err) => console.log(err));
  };

  const markAssignmentAsPaid = () => {
    const data = {
      id: otherServiceData.id,
      Paid: true,
      Invoiced: true,
    };

    api
      .post("/services/markServicePaid", {
        data: data,
      })
      .then((response) => {
        triggerPageRefresh(
          "Interpretation Assignment Marked as Checked",
          theme,
        );
        closeDialog();
      })
      .catch((err) => console.log(err));
  };

  const markAssignmentAsUnpaid = () => {
    const data = {
      id: otherServiceData.id,
      Paid: false,
      Invoiced: false,
      InvoiceID: null,
    };

    api
      .post("/services/markServicePaid", {
        data: data,
      })
      .then((response) => {
        triggerPageRefresh("Assignment Marked as Unpaid", theme);
        closeDialog();
      })
      .catch((err) => console.log(err));
  };

  // Deletes the note passed in (2026-09-23 fix): the dialog's confirm callback
  // previously called deleteNote() from the render where selectedServiceNote
  // was still null (the state update had not re-rendered yet), crashing on
  // note.id. The note is now passed explicitly through the closure.
  const deleteNote = (note = selectedServiceNote) => {
    if (!note?.id) return;
    api
      .post("/services/updateNote", {
        data: {
          NoteID: note.id,
          IsDeleted: 1,
        },
      })
      .then((response) => {
        if (socket && otherServiceData?.id) {
          socket.emit("assignment:saved", {
            modelType: "OtherService",
            recordId: otherServiceData.id,
            changeType: "Note",
            changeAction: "Deleted",
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
        }
        triggerPageRefresh("Note Deleted Sucessfully", theme);
        closeDialog();
      })
      .catch((err) => console.log(err));
  };

  const deleteService = () => {
    const dataOld = {
      Assignment: {
        id: otherServiceData.id,
        Deleted: 0,
      },
    };

    const dataNew = {
      Assignment: {
        id: otherServiceData.id,
        Deleted: 1,
      },
    };

    const allData = {
      Old: dataOld,
      New: dataNew,
    };

    api
      .post("/services/deleteService", {
        data: allData,
      })
      .then((response) => {
        triggerPageRefresh("Service Deleted Sucessfully", theme);
        const navBackTo =
          overlayData?.ComingFrom ?? data?.ComingFrom ?? "/otherServices";
        navigate(`${navBackTo}/`);
      })
      .catch((err) => console.log(err));
  };

  const formatTrackerDate = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("M/D/YYYY") : "";
  };

  const sanitizeCell = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    return /["\t\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const buildTrackerRow = () => {
    const notesText = (otherServiceData.ServiceNotes || [])
      .map((note) => note?.Note)
      .filter(Boolean)
      .join("\n");

    const normalizedMargin =
      otherServiceData.Margin === null || otherServiceData.Margin === undefined
        ? ""
        : Number(otherServiceData.Margin) / 100;

    const notarizationCost = otherServiceData.NotarizationOrCOTCost;
    const notarizedOrCot =
      notarizationCost && Number(notarizationCost) !== 0 ? "True" : "";

    const paidValue = otherServiceData.Paid ? "True" : "";

    const formattedDeliveryDate = formatTrackerDate(
      otherServiceData.DeliveryDate,
    );

    const privateNotes =
      otherServiceData.PrivateNotes || otherServiceData.PrivateNote || "";

    const columns = [
      formatTrackerDate(otherServiceData.Date),
      formatTrackerDate(otherServiceData.Deadline),
      otherServiceData.ProjectNumber,
      otherServiceData.Client?.Account,
      otherServiceData.Requestor?.Name,
      otherServiceData.OtherServiceStatus,
      otherServiceData.AccountCharge,
      otherServiceData.Details,
      otherServiceData.Linguist?.Name,
      otherServiceData.TotalCost,
      otherServiceData.AdditionalCost,
      otherServiceData.LinguistPay,
      otherServiceData.Profit,
      normalizedMargin,
      otherServiceData.Language?.Name,
      targetLanguages,
      "",
      notesText,
      otherServiceData.UnitType?.Name || "N/A",
      otherServiceData.UnitCount || otherServiceData.WordCount || "N/A",
      otherServiceData.NumberOfLanguages,
      otherServiceData.RateType,
      otherServiceData.Rate,
      notarizedOrCot,
      otherServiceData.NotarizationOrCOTCost,
      otherServiceData.DeliveryType,
      otherServiceData.DeliveryCost,
      otherServiceData.PaymentType,
      otherServiceData.PaymentFees,
      formattedDeliveryDate,
      paidValue,
      otherServiceData.ServiceSubtype?.Name,
      otherServiceData.CancellationReason,
      privateNotes,
    ];

    return columns.map(sanitizeCell).join("\t");
  };

  const showManualCopyDialog = (trackerRow) => {
    openDialog({
      theme,
      title: "Clipboard Blocked",
      message: "", // Render instruction in custom content for consistency
      customContent: (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2">
            Please click the content below and manually copy it, then paste it
            into the Tracker.
          </Typography>
          <Box
            component="textarea"
            value={trackerRow}
            readOnly
            sx={{
              width: "100%",
              minHeight: 160,
              p: 1,
              fontFamily: "monospace",
              fontSize: 14,
              borderRadius: 1,
              borderColor: theme.palette.divider,
              bgcolor: theme.palette.background.default,
            }}
            onFocus={(e) => e.target.select()}
          />
        </Box>
      ),
      tertiaryAction: null,
      secondaryAction: "Close",
      primaryAction: null,
      tertiaryOnClick: null,
      secondaryOnClick: () => closeDialog(),
      primaryOnClick: null,
    });
  };

  const handleCopyTrackerRow = async () => {
    const trackerRow = buildTrackerRow();

    // If not secure context (e.g., http or direct IP), clipboard API is blocked.
    if (!window.isSecureContext) {
      showManualCopyDialog(trackerRow);
      return;
    }

    try {
      await navigator.clipboard.writeText(trackerRow);
      showSnackbar("Tracker Row copied successfully.", theme, "success");
    } catch (error) {
      console.error("Error copying tracker row:", error);
      showManualCopyDialog(trackerRow);
    }
  };

  return (
    <Box
      //borderRadius="1.5rem"
      //m=".5rem 1.5rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`, // Take full viewport height minus margin -- DONT CHANGE THIS
      }}
    >
      <Box
        id="test"
        borderRadius="0 0 1.5rem 1.5rem"
        bgcolor={theme.palette.background.alt}
        p={isSmallScreen ? "0.5rem" : "0.5rem 1rem"}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          overflowY: isSmallScreen ? "hidden" : "auto",
          overflowX: "hidden",
        }}
      >
        {isSmallScreen ? (
          // ==================== MOBILE LAYOUT ====================
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
            }}
          >
            <Tabs
              value={mobileTabValue}
              onChange={handleMobileTabsChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                minHeight: "48px",
                bgcolor: theme.palette.background.alt,
                boxShadow: "0 6px 12px -10px rgba(0, 0, 0, 0.35)",
                flexShrink: 0,
              }}
            >
              {showMobileOverview ? <CustomTab label="Overview" /> : null}
              <CustomTab label="Details" />
              <CustomTab label="Tasks" />
              <CustomTab label="Notes" />
              <CustomTab label="Actions" />
              <CustomTab label="History" />
            </Tabs>

            <Box
              ref={mobileScrollRef}
              sx={{
                flex: 1,
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 4,
                minHeight: 0,
                overflowY: "auto",
              }}
            >
              {showMobileOverview ? (
                <Box
                  ref={overviewRef}
                  data-index={0}
                  sx={{ scrollMarginTop: "72px" }}
                >
                  <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                    Overview
                  </Typography>
                  <Grid container spacing={2}>
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 6 }}>
                        <Card
                          big
                          subtitle="Number"
                          content={otherServiceData.ProjectNumber}
                          theme={theme}
                          onClick={() => {
                            navigator.clipboard.writeText(
                              otherServiceData.ProjectNumber,
                            );
                            showSnackbar(
                              "Project number copied to clipboard.",
                              theme,
                              "info",
                            );
                          }}
                        />
                      </Grid>
                    )}
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 6 }}>
                        <Card
                          big
                          subtitle="Status"
                          content={
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 1.25,
                                py: 0.25,
                                borderRadius: "8px",
                                backgroundColor: getStatusColor(
                                  theme,
                                  otherServiceData.OtherServiceStatus || "N/A",
                                ),
                                color: theme.palette.background.default,
                                fontSize: "inherit",
                                fontWeight: 700,
                                lineHeight: 1.2,
                                textAlign: "center",
                              }}
                            >
                              {otherServiceData.OtherServiceStatus || "N/A"}
                            </Box>
                          }
                          theme={theme}
                        />
                      </Grid>
                    )}
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12 }}>
                        <Card
                          big
                          subtitle="Date"
                          content={formatDateLong(otherServiceData.Date)}
                          theme={theme}
                        />
                      </Grid>
                    )}
                    {otherServiceData.Deadline ? (
                      <Grid size={{ xs: 12 }}>
                        <Card
                          big
                          subtitle="Deadline"
                          content={formatDateLong(otherServiceData.Deadline)}
                          theme={theme}
                        />
                      </Grid>
                    ) : null}
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12 }}>
                        <Card
                          big
                          subtitle="Invoice"
                          content={
                            otherServiceData.Invoice?.Number ||
                            (otherServiceData.Invoiced
                              ? otherServiceData.Client?.Account ===
                                "Private Persons"
                                ? "Paid Cash"
                                : "Invoiced: Tracker"
                              : "Not Invoiced")
                          }
                          theme={theme}
                          onClick={
                            invoiceNumber
                              ? () =>
                                  navigate(`/invoices/${invoiceID}`, {
                                    state: {
                                      ComingFrom: `/otherServices/${id}`,
                                    },
                                  })
                              : null
                          }
                        />
                      </Grid>
                    )}
                    <Grid size={{ xs: 12 }}>
                      <Card
                        big
                        subtitle="Target Languages"
                        content={targetLanguages}
                        theme={theme}
                      />
                    </Grid>
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12 }}>
                        <Card
                          big
                          subtitle="Account Charge"
                          content={`$${accountChargeValue || 0}`}
                          theme={theme}
                        />
                      </Grid>
                    )}
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                          subtitle="Linguist Pay"
                          content={`$${linguistPayValue || 0}`}
                          theme={theme}
                        />
                      </Grid>
                    )}
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                          big
                          subtitle="Total Cost"
                          content={`$${totalCostValue || 0}`}
                          theme={theme}
                        />
                      </Grid>
                    )}
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                          subtitle="Profit"
                          content={formatCurrencyValue(otherServiceData.Profit)}
                          theme={theme}
                        />
                      </Grid>
                    )}
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                          subtitle="Margin"
                          content={
                            otherServiceData.Margin === 0
                              ? "0%"
                              : otherServiceData.Margin === 100
                                ? "100%"
                                : `${otherServiceData.Margin}%`
                          }
                          theme={theme}
                        />
                      </Grid>
                    )}
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12 }} sx={{ mt: 2, mb: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            userSelect: "none",
                            "&:hover": { opacity: 0.8 },
                          }}
                          onClick={() => setShowOtherCosts(!showOtherCosts)}
                        >
                          {showOtherCosts ? (
                            <KeyboardArrowDownRoundedIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowRightRoundedIcon fontSize="small" />
                          )}
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              fontSize: "0.95rem",
                            }}
                          >
                            Other Costs
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {!isModernDetailsEnabled && showOtherCosts && (
                      <>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Card
                            subtitle="Additional Cost"
                            content={formatAmount(additionalCostValue)}
                            theme={theme}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Card
                            subtitle="Notarization/COT Cost"
                            content={formatAmount(notarizationCostValue)}
                            theme={theme}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Card
                            subtitle="Delivery Cost"
                            content={formatAmount(deliveryCostValue)}
                            theme={theme}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Card
                            subtitle="Payment Fees"
                            content={formatAmount(paymentFeesValue)}
                            theme={theme}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              ) : null}

              <Box
                ref={detailsRef}
                data-index={1}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                  Details
                </Typography>
                {renderLegacyDetails()}
              </Box>

              <Box
                ref={tasksRef}
                data-index={2}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                  Tasks
                </Typography>
                <DragDropContext onDragEnd={handleTaskDragEnd}>
                  <TaskBoard
                    statuses={taskStatuses}
                    tasks={tasksForService}
                    isLoading={tasksLoading}
                    onDragEnd={handleTaskDragEnd}
                    renderTaskCard={renderTaskCard}
                    creatingTaskForStatus={null}
                    newTaskTitle=""
                    setNewTaskTitle={() => {}}
                    handleCreateInlineTask={() => {}}
                    setCreatingTaskForStatus={() => {}}
                    enableInlineCreate={false}
                    columnsOverride={1}
                    theme={theme}
                    heightFraction={1}
                    rowHeightFraction={0.8}
                    containerPadding={"0.25rem"}
                    containerMargin={null}
                    gridSpacing={2}
                    externalDragContext={true}
                  />
                </DragDropContext>
              </Box>

              <Box
                ref={notesRef}
                data-index={3}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography
                  variant="h3"
                  sx={{ mb: 2, mt: 3, fontWeight: "bold" }}
                >
                  Notes
                </Typography>
                {otherServiceData &&
                (otherServiceData.ServiceNotes || []).length > 0 ? (
                  <>
                    <Grid container spacing={2}>
                      {(otherServiceData.ServiceNotes || []).map(
                        (serviceNote, index) => (
                          <Grid
                            key={serviceNote.Note + "//" + index}
                            size={{ xs: 12 }}
                          >
                            <NoteCard
                              title={serviceNote.Note}
                              items={
                                serviceNote.CreatedOn
                                  ? (serviceNote?.Member?.Name || "N/A") +
                                    " - " +
                                    formatDateLongWithTime(
                                      serviceNote.CreatedOn,
                                    )
                                  : serviceNote?.Member?.Name || "N/A"
                              }
                              justify="start"
                              onEdit={() => handleEdit(serviceNote)}
                              onDelete={() => handleDelete(serviceNote)}
                            />
                          </Grid>
                        ),
                      )}
                    </Grid>
                  </>
                ) : (
                  <Box sx={{ textAlign: "center", p: 4 }}>
                    <Typography variant="h4">
                      There are no notes on this assignment, add one by clicking
                      the (+) button
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box
                ref={actionsRef}
                data-index={4}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                  Actions
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box width="100%">
                  <Typography
                    sx={{
                      m: "0.25rem 0 0 0.5rem",
                      color: theme.palette.primary.main,
                      fontSize: 13,
                    }}
                  >
                    Assignment
                  </Typography>

                  <ListItem key="Edit" disablePadding>
                    <ListItemButton
                      sx={{
                        color: theme.palette.primary.main,
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.divider}`,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => {
                        const secondData = {
                          ...otherServiceData,
                          Action: OVERLAY_NAMES.OTHERSERVICE_ACTION_EDIT,
                          Clients: listOfClients,
                          Linguists: listOfLinguists,
                          Languages: listOfLanguages,
                          OtherServiceStatuses: listOfStatuses,
                          OtherServiceTypes: listOfTypes,
                          ProjectNumber: otherServiceData.ProjectNumber,
                          DeliveryTypes: deliveryTypes,
                          PaymentTypes: paymentTypes,
                        };
                        setOverlayData(secondData);
                        setOverlayName(OVERLAY_NAMES.OTHERSERVICE_NEW_EDIT);
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          mr: "-1rem",
                          color: theme.palette.primary.main,
                        }}
                      >
                        <EditRoundedIcon
                          sx={{
                            fontSize: 28,
                            color: theme.palette.primary.main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Edit Assignment" />
                    </ListItemButton>
                  </ListItem>

                  {otherServiceData.OtherServiceStatus &&
                  (otherServiceData.OtherServiceStatus.includes("Completed") ||
                    otherServiceData.OtherServiceStatus.includes(
                      "Cancelled",
                    )) ? null : (
                    <ListItem key="Complete" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          const data = {
                            ...otherServiceData,
                            Action: OVERLAY_NAMES.OTHERSERVICE_ACTION_COMPLETE,
                            Clients: listOfClients,
                            Linguists: listOfLinguists,
                            Languages: listOfLanguages,
                            OtherServiceStatuses: listOfStatuses,
                            OtherServiceTypes: listOfTypes,
                            ProjectNumber: latestProjectNumber,
                            DeliveryTypes: deliveryTypes,
                            PaymentTypes: paymentTypes,
                          };

                          setOverlayData(data);
                          setOverlayName(OVERLAY_NAMES.OTHERSERVICE_NEW_EDIT);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <TaskRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Complete Assignment" />
                      </ListItemButton>
                    </ListItem>
                  )}

                  <ListItem key="Check" disablePadding>
                    <ListItemButton
                      sx={{
                        color: theme.palette.primary.main,
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.divider}`,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => {
                        openDialog({
                          theme: theme,
                          title: "Mark As Checked",
                          message:
                            "This assignment will be marked as Checked by you on today's date",
                          customContent: null,
                          tertiaryAction: null,
                          secondaryAction: "Close",
                          primaryAction: "Confirm",
                          tertiaryOnClick: null,
                          secondaryOnClick: () => {
                            closeDialog();
                          },
                          primaryOnClick: () => {
                            markAssignmentAsChecked();
                            closeDialog();
                          },
                        });
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          mr: "-1rem",
                          color: theme.palette.primary.main,
                        }}
                      >
                        <VerifiedUserRoundedIcon
                          sx={{
                            fontSize: 28,
                            color: theme.palette.primary.main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Mark As Checked" />
                    </ListItemButton>
                  </ListItem>

                  {canMarkAsPaid ? (
                    <ListItem key="Pay" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          openDialog({
                            theme: theme,
                            title: "Mark As Paid",
                            message:
                              otherServiceData.Client?.Account !==
                              "Private Persons"
                                ? "This assignment will be marked as Paid, and won't be invoiced, are you sure you want to continue?"
                                : "This assignment will be marked as Paid",
                            customContent: null,
                            tertiaryAction: null,
                            secondaryAction: "Close",
                            primaryAction: "Confirm",
                            tertiaryOnClick: null,
                            secondaryOnClick: () => {
                              closeDialog();
                            },
                            primaryOnClick: () => {
                              markAssignmentAsPaid();
                              closeDialog();
                            },
                          });
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <CreditScoreRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Mark As Paid" />
                      </ListItemButton>
                    </ListItem>
                  ) : canMarkAsUnpaid ? (
                    <ListItem key="Unpay" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          openDialog({
                            theme: theme,
                            title: "Mark As Unpaid",
                            message:
                              "This assignment will be marked as Unpaid and returned to not invoiced status, are you sure you want to continue?",
                            customContent: null,
                            tertiaryAction: null,
                            secondaryAction: "Close",
                            primaryAction: "Confirm",
                            tertiaryOnClick: null,
                            secondaryOnClick: () => {
                              closeDialog();
                            },
                            primaryOnClick: () => {
                              markAssignmentAsUnpaid();
                              closeDialog();
                            },
                          });
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <RestartAltRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Mark As Unpaid" />
                      </ListItemButton>
                    </ListItem>
                  ) : null}

                  <ListItem key="ChangeCategory" disablePadding>
                    <ListItemButton
                      sx={{
                        color: theme.palette.primary.main,
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.divider}`,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => {
                        const secondData = {
                          ServiceID: otherServiceData.id,
                          Category: otherServiceData.ServiceCategory,
                        };

                        setOverlayData(secondData);

                        setOverlayName(OVERLAY_NAMES.SERVICE_CHANGE_CATEGORY);
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          mr: "-1rem",
                          color: theme.palette.primary.main,
                        }}
                      >
                        <CategoryRoundedIcon
                          sx={{
                            fontSize: 28,
                            color: theme.palette.primary.main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Change Category" />
                    </ListItemButton>
                  </ListItem>

                  <ListItem key="DeleteAssignment" disablePadding>
                    <ListItemButton
                      sx={{
                        color: theme.palette.primary.main,
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.divider}`,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => {
                        openDialog({
                          theme: theme,
                          title: "Delete Assignment",
                          message:
                            "Do you really wish to delete this assignment?",
                          customContent: null,
                          tertiaryAction: null,
                          secondaryAction: "Close",
                          primaryAction: "Delete",
                          tertiaryOnClick: null,
                          secondaryOnClick: () => {
                            closeDialog();
                          },
                          primaryOnClick: () => {
                            deleteService();
                            closeDialog();
                          },
                        });
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          mr: "-1rem",
                          color: theme.palette.primary.main,
                        }}
                      >
                        <DeleteRoundedIcon
                          sx={{
                            fontSize: 28,
                            color: theme.palette.primary.main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Delete Assignment" />
                    </ListItemButton>
                  </ListItem>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box width="100%">
                  <Typography
                    sx={{
                      m: "0.25rem 0 0 0.5rem",
                      color: theme.palette.primary.main,
                      fontSize: 13,
                    }}
                  >
                    Tasks & Notes
                  </Typography>

                  <ListItem key="AddTask" disablePadding>
                    <ListItemButton
                      sx={{
                        color: theme.palette.primary.main,
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.divider}`,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => {
                        setOverlayData({
                          Action: OVERLAY_NAMES.TASK_ACTION_NEW,
                          Task: {
                            EntityType: "Service",
                            EntityID: id,
                            ServiceType: "Other Service",
                          },
                          Service: {
                            id: otherServiceData.id,
                            ProjectNumber: otherServiceData.ProjectNumber,
                            Details: otherServiceData.Details,
                            ServiceCategory: otherServiceData.ServiceCategory,
                          },
                        });
                        setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          mr: "-1rem",
                          color: theme.palette.primary.main,
                        }}
                      >
                        <TaskRoundedIcon
                          sx={{
                            fontSize: 28,
                            color: theme.palette.primary.main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Add Task" />
                    </ListItemButton>
                  </ListItem>

                  <ListItem key="AddNote" disablePadding>
                    <ListItemButton
                      sx={{
                        color: theme.palette.primary.main,
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.divider}`,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => {
                        const data = {
                          ServiceID: otherServiceData.id,
                          MemberID: currentUser.id,
                          ModelType: "OtherService",
                          Action: OVERLAY_NAMES.SERVICE_NOTE_ACTION_NEW,
                        };

                        setOverlayData(data);

                        setOverlayName(OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT);
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          mr: "-1rem",
                          color: theme.palette.primary.main,
                        }}
                      >
                        <CommentRoundedIcon
                          sx={{
                            fontSize: 28,
                            color: theme.palette.primary.main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Add Note" />
                    </ListItemButton>
                  </ListItem>

                  {otherServiceData.Requestor?.Email !== null &&
                  otherServiceData.Requestor?.Name !== "N/A" ? (
                    <>
                      <Divider
                        sx={{
                          my: 2,
                        }}
                      />

                      <Typography
                        sx={{
                          m: "0.25rem 0 0 0.5rem",
                          color: theme.palette.primary.main,
                          fontSize: 13,
                        }}
                      >
                        Communications
                      </Typography>
                    </>
                  ) : null}

                  {otherServiceData.Requestor?.Email !== null &&
                  otherServiceData.Requestor?.Name !== "N/A" ? (
                    <Typography
                      sx={{
                        m: "0.25rem 0 0 0.5rem",
                        color: theme.palette.primary.main,
                        fontSize: 13,
                      }}
                    >
                      {"Requestor: " +
                        (otherServiceData.Requestor?.Name || "N/A")}
                    </Typography>
                  ) : null}

                  {otherServiceData.Requestor?.Email !== null &&
                  otherServiceData.Requestor?.Name !== "N/A" ? (
                    <ListItem key="Requestor Email" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          const email = otherServiceData.Requestor?.Email;
                          const subject =
                            "Regarding Assignment N° " +
                            otherServiceData.ProjectNumber;
                          const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
                            subject,
                          )}`;
                          window.open(mailtoLink, "_blank");
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <EmailRoundedIcon
                            sx={{
                              fontSize: 24,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Send Email" />
                      </ListItemButton>
                    </ListItem>
                  ) : null}

                  <Divider
                    sx={{
                      my: 2,
                    }}
                  />

                  <Typography
                    sx={{
                      m: "0.25rem 0 0 0.5rem",
                      color: theme.palette.primary.main,
                      fontSize: 13,
                    }}
                  >
                    Utilities
                  </Typography>

                  <ListItem key="CopyTrackerRow" disablePadding>
                    <ListItemButton
                      sx={{
                        color: theme.palette.primary.main,
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.divider}`,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={handleCopyTrackerRow}
                    >
                      <ListItemIcon
                        sx={{
                          mr: "-1rem",
                          color: theme.palette.primary.main,
                        }}
                      >
                        <ContentCopyRoundedIcon
                          sx={{
                            fontSize: 24,
                            color: theme.palette.primary.main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Copy Tracker Row" />
                    </ListItemButton>
                  </ListItem>

                  <ListItem key="OneDrive" disablePadding>
                    <ListItemButton
                      sx={{
                        color: theme.palette.primary.main,
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.divider}`,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => {
                        const oneDriveLink =
                          "https://communicationessentialsllc-my.sharepoint.com/personal/amer_communicationessentialsllc_com/_layouts/15/onedrive.aspx?id=%2Fpersonal%2Famer%5Fcommunicationessentialsllc%5Fcom%2FDocuments%2FCommunication%20Essentials%2FAssignments%2FOtherServices%202025%2F" +
                          otherServiceData.ProjectNumber;

                        window.open(oneDriveLink, "_blank");
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          mr: "-1rem",
                          color: theme.palette.primary.main,
                        }}
                      >
                        <CloudIcon
                          sx={{
                            fontSize: 24,
                            color: theme.palette.primary.main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Open One Drive Link" />
                    </ListItemButton>
                  </ListItem>
                </Box>
              </Box>

              <Box
                ref={historyRef}
                data-index={5}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                  History
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <History serviceHistories={serviceHistories} />
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ flex: 1, width: "100%" }}>
            <Grid
              size={{ xs: 12, sm: 12, md: "auto" }}
              sx={{
                display: isModernDetailsEnabled ? "none" : "flex",
                flexDirection: "column",
                minWidth: "300px",
                width: { md: "300px" },
                maxWidth: { md: "300px" },
                flexShrink: 0,
                height: "100%",
              }}
            >
              <Box
                sx={{
                  overflowY: "auto",
                  flex: 1,
                  width: "100%",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    flexGrow: 1,
                    p: ".75rem 0",
                    overflowY: "auto",
                    height: "100%",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h4" fontWeight="bold">
                        General Information
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid container size={{ xs: 12 }} spacing={1}>
                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Number"
                          content={otherServiceData.ProjectNumber}
                          theme={theme}
                          onClick={() => {
                            navigator.clipboard.writeText(
                              otherServiceData.ProjectNumber,
                            );
                            showSnackbar(
                              "Project number copied to clipboard.",
                              theme,
                              "info",
                            );
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Status"
                          content={
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 1.25,
                                py: 0.25,
                                borderRadius: "8px",
                                backgroundColor: getStatusColor(
                                  theme,
                                  otherServiceData.OtherServiceStatus || "N/A",
                                ),
                                color: theme.palette.background.default,
                                fontSize: "inherit",
                                fontWeight: 700,
                                lineHeight: 1.2,
                                textAlign: "center",
                              }}
                            >
                              {otherServiceData.OtherServiceStatus || "N/A"}
                            </Box>
                          }
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Date"
                          content={formatDateLong(otherServiceData.Date)}
                          theme={theme}
                        />
                      </Grid>
                      {otherServiceData.Deadline ? (
                        <Grid
                          size={{ xs: 12 }}
                          container
                          spacing={1}
                          p=".25rem"
                        >
                          <Card
                            big
                            subtitle="Deadline"
                            content={formatDateLong(otherServiceData.Deadline)}
                            theme={theme}
                          />
                        </Grid>
                      ) : null}
                      <Grid size={{ xs: 12 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Invoice"
                          content={
                            otherServiceData.Invoice?.Number ||
                            (otherServiceData.Invoiced
                              ? otherServiceData.Client?.Account ===
                                "Private Persons"
                                ? "Paid Cash"
                                : "Invoiced: Tracker"
                              : "Not Invoiced")
                          }
                          theme={theme}
                          onClick={
                            invoiceNumber
                              ? () =>
                                  navigate(`/invoices/${invoiceID}`, {
                                    state: {
                                      ComingFrom: `/otherServices/${id}`,
                                    },
                                  })
                              : null
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Target Languages"
                          content={targetLanguages}
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Account Charge"
                          content={`$${accountChargeValue || 0}`}
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          subtitle="Linguist Pay"
                          content={`$${linguistPayValue || 0}`}
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Total Cost"
                          content={`$${totalCostValue || 0}`}
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          subtitle="Profit"
                          content={formatCurrencyValue(otherServiceData.Profit)}
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          subtitle="Margin"
                          content={
                            otherServiceData.Margin === 0
                              ? "0%"
                              : otherServiceData.Margin === 100
                                ? "100%"
                                : `${otherServiceData.Margin}%`
                          }
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }} sx={{ mt: 2, mb: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            userSelect: "none",
                            "&:hover": { opacity: 0.8 },
                          }}
                          onClick={() => setShowOtherCosts(!showOtherCosts)}
                        >
                          {showOtherCosts ? (
                            <KeyboardArrowDownRoundedIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowRightRoundedIcon fontSize="small" />
                          )}
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              fontSize: "0.95rem",
                            }}
                          >
                            Other Costs
                          </Typography>
                        </Box>
                      </Grid>
                      {showOtherCosts && (
                        <>
                          <Grid
                            size={{ xs: 6 }}
                            container
                            spacing={1}
                            p=".25rem"
                          >
                            <Card
                              subtitle="Additional Cost"
                              content={formatAmount(additionalCostValue)}
                              theme={theme}
                            />
                          </Grid>
                          <Grid
                            size={{ xs: 6 }}
                            container
                            spacing={1}
                            p=".25rem"
                          >
                            <Card
                              subtitle="Notarization/COT Cost"
                              content={formatAmount(notarizationCostValue)}
                              theme={theme}
                            />
                          </Grid>
                          <Grid
                            size={{ xs: 6 }}
                            container
                            spacing={1}
                            p=".25rem"
                          >
                            <Card
                              subtitle="Delivery Cost"
                              content={formatAmount(deliveryCostValue)}
                              theme={theme}
                            />
                          </Grid>
                          <Grid
                            size={{ xs: 6 }}
                            container
                            spacing={1}
                            p=".25rem"
                          >
                            <Card
                              subtitle="Payment Fees"
                              content={formatAmount(paymentFeesValue)}
                              theme={theme}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>

            <Divider
              orientation={isSmallScreen ? "horizontal" : "vertical"}
              flexItem
              sx={{ display: isModernDetailsEnabled ? "none" : "block" }}
            />

            <Grid
              size={{ xs: 12, sm: 12, md: "grow" }}
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                flex: "1 1 auto",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabsChange}
                    aria-label="basic tabs example"
                    sx={{ bgcolor: theme.palette.background.alt }}
                  >
                    <CustomTab label="Details" {...a11yProps(0)} />
                    <CustomTab label="Tasks" {...a11yProps(1)} />
                    <CustomTab label="Notes" {...a11yProps(2)} />
                    <CustomTab label="History" {...a11yProps(3)} />
                  </Tabs>
                </Box>

                <DragDropContext onDragEnd={handleTaskDragEnd}>
                  <CustomTabPanel
                    value={tabValue}
                    index={0}
                    key="Tab One"
                    sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                  >
                    <Box
                      sx={{
                        flexGrow: 1,
                        p: ".75rem 0.25rem",
                        overflowY: "auto",
                        height: "100%",
                      }}
                    >
                      {renderLegacyDetails()}
                    </Box>
                  </CustomTabPanel>

                  <CustomTabPanel
                    value={tabValue}
                    index={1}
                    key="Tab Two"
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "visible",
                      minHeight: 0,
                    }}
                  >
                    <TaskBoard
                      statuses={taskStatuses}
                      tasks={tasksForService}
                      isLoading={tasksLoading}
                      onDragEnd={handleTaskDragEnd}
                      renderTaskCard={renderTaskCard}
                      creatingTaskForStatus={null}
                      newTaskTitle=""
                      setNewTaskTitle={() => {}}
                      handleCreateInlineTask={() => {}}
                      setCreatingTaskForStatus={() => {}}
                      enableInlineCreate={false}
                      columnsOverride={2}
                      theme={theme}
                      heightFraction={1}
                      rowHeightFraction={0.5}
                      containerPadding={"0.25rem"}
                      containerMargin="0"
                      gridSpacing={2}
                      externalDragContext={true}
                    />

                    {/* Removed priority color code popover */}
                  </CustomTabPanel>
                  <CustomTabPanel
                    value={tabValue}
                    index={2}
                    key="Tab Three"
                    sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                  >
                    {otherServiceData &&
                    (otherServiceData.ServiceNotes || []).length > 0 ? (
                      <Box
                        sx={{
                          flexGrow: 1,
                          p: ".75rem 0.25rem",
                          overflowY: "auto",
                          height: "100%",
                        }}
                      >
                        <Grid container spacing={2}>
                          {(otherServiceData.ServiceNotes || []).map(
                            (serviceNote, index) => (
                              <Grid
                                key={serviceNote.Note + "//" + index}
                                size={{ xs: 12 }}
                              >
                                <NoteCard
                                  title={serviceNote.Note}
                                  items={
                                    serviceNote.CreatedOn
                                      ? (serviceNote?.Member?.Name || "N/A") +
                                        " - " +
                                        formatDateLongWithTime(
                                          serviceNote.CreatedOn,
                                        )
                                      : serviceNote?.Member?.Name || "N/A"
                                  }
                                  justify="start"
                                  onEdit={() => handleEdit(serviceNote)}
                                  onDelete={() => handleDelete(serviceNote)}
                                />
                              </Grid>
                            ),
                          )}
                        </Grid>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "80%",
                          width: "100%",
                        }}
                      >
                        <Typography variant="h4">
                          There are no notes on this assignment, add one by
                          clicking the (+) button
                        </Typography>
                      </Box>
                    )}
                  </CustomTabPanel>
                  <CustomTabPanel
                    value={tabValue}
                    index={3}
                    key="Tab Four"
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      p: 0,
                    }}
                  >
                    <Box
                      sx={{
                        overflowY: "auto",
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <History serviceHistories={serviceHistories} />
                    </Box>
                  </CustomTabPanel>
                </DragDropContext>
              </Box>
            </Grid>
            <Divider
              orientation={isSmallScreen ? "horizontal" : "vertical"} //TO CHANGE
              flexItem
            />

            <Grid
              size={{ xs: 12, sm: 12, md: "auto" }}
              sx={{
                display: "flex",
                flexDirection: "column",
                width: { md: "240px" },
                minWidth: { md: "240px" },
                maxWidth: { md: "240px" },
                flexShrink: 0,
                height: "100%",
              }}
            >
              <Box
                sx={{
                  overflowY: "auto",
                  flex: 1,
                  width: "100%",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    flexGrow: 1,
                    p: ".75rem 0",
                    overflowY: "auto",
                    height: "100%",
                  }}
                >
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="h5" fontWeight="bold">
                      Actions
                    </Typography>
                  </Grid>

                  <Divider
                    sx={{
                      my: 2,
                    }}
                  />
                  <Box width="100%">
                    <Typography
                      sx={{
                        m: "0.25rem 0 0 0.5rem",
                        color: theme.palette.primary.main,
                        fontSize: 13,
                      }}
                    >
                      Assignment
                    </Typography>

                    <ListItem key="Edit" disablePadding>
                      <ListItemButton
                        sx={{
                          //backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          const secondData = {
                            ...otherServiceData,
                            Action: OVERLAY_NAMES.OTHERSERVICE_ACTION_EDIT,
                            Clients: listOfClients,
                            Linguists: listOfLinguists,
                            Languages: listOfLanguages,
                            OtherServiceStatuses: listOfStatuses,
                            OtherServiceTypes: listOfTypes,
                            ProjectNumber: otherServiceData.ProjectNumber,
                            DeliveryTypes: deliveryTypes,
                            PaymentTypes: paymentTypes,
                          };
                          setOverlayData(secondData);
                          setOverlayName(OVERLAY_NAMES.OTHERSERVICE_NEW_EDIT);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <EditRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Edit Assignment" />
                      </ListItemButton>
                    </ListItem>

                    {otherServiceData.OtherServiceStatus &&
                    (otherServiceData.OtherServiceStatus.includes(
                      "Completed",
                    ) ||
                      otherServiceData.OtherServiceStatus.includes(
                        "Cancelled",
                      )) ? null : (
                      <ListItem key="Complete" disablePadding>
                        <ListItemButton
                          onClick={() => {
                            const data = {
                              ...otherServiceData,
                              Action:
                                OVERLAY_NAMES.OTHERSERVICE_ACTION_COMPLETE,
                              Clients: listOfClients,
                              Linguists: listOfLinguists,
                              Languages: listOfLanguages,
                              OtherServiceStatuses: listOfStatuses,
                              OtherServiceTypes: listOfTypes,
                              ProjectNumber: latestProjectNumber,
                              DeliveryTypes: deliveryTypes,
                              PaymentTypes: paymentTypes,
                            };

                            setOverlayData(data);
                            setOverlayName(OVERLAY_NAMES.OTHERSERVICE_NEW_EDIT);
                          }}
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <TaskRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Complete Assignment" />
                        </ListItemButton>
                      </ListItem>
                    )}

                    <ListItem key="Check" disablePadding>
                      <ListItemButton
                        sx={{
                          //backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          openDialog({
                            theme: theme,
                            title: "Mark As Checked",
                            message:
                              "This assignment will be marked as Checked by you on today's date",
                            //TO DO ADD dialogIconCheck
                            customContent: null,

                            tertiaryAction: null,
                            secondaryAction: "Close",
                            primaryAction: "Confirm",

                            tertiaryOnClick: null,

                            secondaryOnClick: () => {
                              closeDialog();
                            },

                            primaryOnClick: () => {
                              markAssignmentAsChecked();
                              closeDialog();
                            },
                          });
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <VerifiedUserRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Mark As Checked" />
                      </ListItemButton>
                    </ListItem>

                    {canMarkAsPaid ? (
                      <ListItem key="Pay" disablePadding>
                        <ListItemButton
                          sx={{
                            //backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            openDialog({
                              theme: theme,
                              title: "Mark As Paid",
                              message:
                                otherServiceData.Client?.Account !==
                                "Private Persons"
                                  ? "This assignment will be marked as Paid, and won't be invoiced, are you sure you want to continue?"
                                  : "This assignment will be marked as Paid",
                              customContent: null,

                              tertiaryAction: null,
                              secondaryAction: "Close",
                              primaryAction: "Confirm",

                              tertiaryOnClick: null,

                              secondaryOnClick: () => {
                                closeDialog();
                              },

                              primaryOnClick: () => {
                                markAssignmentAsPaid();
                                closeDialog();
                              },
                            });
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <CreditScoreRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Mark As Paid" />
                        </ListItemButton>
                      </ListItem>
                    ) : canMarkAsUnpaid ? (
                      <ListItem key="Unpay" disablePadding>
                        <ListItemButton
                          sx={{
                            //backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            openDialog({
                              theme: theme,
                              title: "Mark As Unpaid",
                              message:
                                "This assignment will be marked as Unpaid and returned to not invoiced status, are you sure you want to continue?",
                              customContent: null,

                              tertiaryAction: null,
                              secondaryAction: "Close",
                              primaryAction: "Confirm",

                              tertiaryOnClick: null,

                              secondaryOnClick: () => {
                                closeDialog();
                              },

                              primaryOnClick: () => {
                                markAssignmentAsUnpaid();
                                closeDialog();
                              },
                            });
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <RestartAltRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Mark As Unpaid" />
                        </ListItemButton>
                      </ListItem>
                    ) : null}

                    <ListItem key="ChangeCategory" disablePadding>
                      <ListItemButton
                        sx={{
                          //backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          const secondData = {
                            ServiceID: otherServiceData.id,
                            Category: otherServiceData.ServiceCategory,
                          };

                          setOverlayData(secondData);

                          setOverlayName(OVERLAY_NAMES.SERVICE_CHANGE_CATEGORY);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <CategoryRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Change Category" />
                      </ListItemButton>
                    </ListItem>

                    <ListItem key="DeleteAssignment" disablePadding>
                      <ListItemButton
                        sx={{
                          //backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          openDialog({
                            theme: theme,
                            title: "Delete Assignment",
                            message:
                              "Do you really wish to delete this assignment?",
                            //TO DO ADD dialogIconCheck
                            customContent: null,

                            tertiaryAction: null,
                            secondaryAction: "Close",
                            primaryAction: "Delete",

                            tertiaryOnClick: null,

                            secondaryOnClick: () => {
                              closeDialog();
                            },

                            primaryOnClick: () => {
                              deleteService();
                              closeDialog();
                            },
                          });
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <DeleteRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Delete Assignment" />
                      </ListItemButton>
                    </ListItem>
                  </Box>

                  <Divider
                    sx={{
                      my: 2,
                    }}
                  />

                  <Box width="100%">
                    <Typography
                      sx={{
                        m: "0.25rem 0 0 0.5rem",
                        color: theme.palette.primary.main,
                        fontSize: 13,
                      }}
                    >
                      Tasks & Notes
                    </Typography>

                    <ListItem key="AddTask" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          setOverlayData({
                            Action: OVERLAY_NAMES.TASK_ACTION_NEW,
                            Task: {
                              EntityType: "Service",
                              EntityID: id,
                              ServiceType: "Other Service",
                            },
                            Service: {
                              id: otherServiceData.id,
                              ProjectNumber: otherServiceData.ProjectNumber,
                              Details: otherServiceData.Details,
                              ServiceCategory: otherServiceData.ServiceCategory,
                            },
                          });
                          setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <TaskRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Add Task" />
                      </ListItemButton>
                    </ListItem>

                    <ListItem key="AddNote" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          const data = {
                            ServiceID: otherServiceData.id,
                            MemberID: currentUser.id,
                            ModelType: "OtherService",
                            Action: OVERLAY_NAMES.SERVICE_NOTE_ACTION_NEW,
                          };

                          setOverlayData(data);

                          setOverlayName(OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <CommentRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Add Note" />
                      </ListItemButton>
                    </ListItem>

                    {otherServiceData.Requestor?.Email !== null &&
                    otherServiceData.Requestor?.Name !== "N/A" ? (
                      <>
                        <Divider
                          sx={{
                            my: 2,
                          }}
                        />

                        <Typography
                          sx={{
                            m: "0.25rem 0 0 0.5rem",
                            color: theme.palette.primary.main,
                            fontSize: 13,
                          }}
                        >
                          Communications
                        </Typography>
                      </>
                    ) : null}

                    {otherServiceData.Requestor?.Email !== null &&
                    otherServiceData.Requestor?.Name !== "N/A" ? (
                      <Typography
                        sx={{
                          m: "0.25rem 0 0 0.5rem",
                          color: theme.palette.primary.main,
                          fontSize: 13,
                        }}
                      >
                        {"Requestor: " +
                          (otherServiceData.Requestor?.Name || "N/A")}
                      </Typography>
                    ) : null}

                    {otherServiceData.Requestor?.Email !== null &&
                    otherServiceData.Requestor?.Name !== "N/A" ? (
                      <ListItem key="Requestor Email" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                          }}
                          onClick={() => {
                            const email = otherServiceData.Requestor?.Email;
                            const subject =
                              "Regarding Assignment N° " +
                              otherServiceData.ProjectNumber;
                            const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
                              subject,
                            )}`;
                            window.open(mailtoLink, "_blank");
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <EmailRoundedIcon
                              sx={{
                                fontSize: 24,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Send Email" />
                        </ListItemButton>
                      </ListItem>
                    ) : null}

                    <Divider
                      sx={{
                        my: 2,
                      }}
                    />

                    <Typography
                      sx={{
                        m: "0.25rem 0 0 0.5rem",
                        color: theme.palette.primary.main,
                        fontSize: 13,
                      }}
                    >
                      Utilities
                    </Typography>

                    <ListItem key="CopyTrackerRow" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={handleCopyTrackerRow}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <ContentCopyRoundedIcon
                            sx={{
                              fontSize: 24,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Copy Tracker Row" />
                      </ListItemButton>
                    </ListItem>

                    <ListItem key="OneDrive" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          const oneDriveLink =
                            "https://communicationessentialsllc-my.sharepoint.com/personal/amer_communicationessentialsllc_com/_layouts/15/onedrive.aspx?id=%2Fpersonal%2Famer%5Fcommunicationessentialsllc%5Fcom%2FDocuments%2FCommunication%20Essentials%2FAssignments%2FOtherServices%202025%2F" +
                            otherServiceData.ProjectNumber;

                          window.open(oneDriveLink, "_blank");
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <CloudIcon
                            sx={{
                              fontSize: 24,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Open One Drive Link" />
                      </ListItemButton>
                    </ListItem>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default OtherServiceProfile;
